'use client'

/**
 * Tracks the user's recent meaningful sound interactions in
 * `localStorage['tls.recent.v1']` (FIFO cap 30). Powers the client-side
 * taste vector that personalizes /sounds/foryou (see lib/personalization.ts,
 * Phase 2 of the For You feed plan).
 *
 * "Meaningful" = completed (≥80% played) or skipped (<20% played). We do not
 * record neutral plays — they're noise in the scoring model.
 *
 * Writes happen via `appendRecentPlay()` from contexts/audio-player-context.tsx
 * (the only place that knows when a preview reaches a terminal state). The
 * React hook `useRecentPlays()` is read-only.
 */

import { useSyncExternalStore } from 'react'

import { readStorage, removeStorage, writeStorage } from '@/lib/safe-storage'

const STORAGE_KEY = 'tls.recent.v1'
const CAP = 30

export type RecentPlayClass = 'completed' | 'skipped'

export interface RecentPlay {
  id: string
  category: string
  vibe_tags: string[]
  tags: string[]
  class: RecentPlayClass
  played_pct: number
  ts: number
}

const CHANGE_EVENT = 'tls:recent-plays-changed'

function safeRead(): RecentPlay[] {
  try {
    const raw = readStorage(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (entry): entry is RecentPlay =>
        entry &&
        typeof entry.id === 'string' &&
        typeof entry.ts === 'number' &&
        (entry.class === 'completed' || entry.class === 'skipped'),
    )
  } catch {
    return []
  }
}

function safeWrite(entries: RecentPlay[]): void {
  if (typeof window === 'undefined') return
  // Only notify subscribers when the write actually landed — on a client with
  // storage disabled, waking every useRecentPlays() to re-read an unchanged
  // (empty) store is pure churn.
  if (writeStorage(STORAGE_KEY, JSON.stringify(entries))) {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
  }
}

/** FIFO append with cap. Most-recent entries live at the END of the array. */
export function appendRecentPlay(entry: RecentPlay): void {
  const existing = safeRead()
  // Drop any previous entry for the same sound id so the most-recent class
  // is the one that counts.
  const deduped = existing.filter((e) => e.id !== entry.id)
  deduped.push(entry)
  const trimmed = deduped.length > CAP ? deduped.slice(deduped.length - CAP) : deduped
  safeWrite(trimmed)
}

// useSyncExternalStore plumbing — subscribe to in-tab change events + the
// native cross-tab `storage` event, snapshot from localStorage. A cached
// snapshot keeps reference equality stable across reads so React can bail
// out cleanly when nothing has changed.
function subscribeRecentPlays(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(CHANGE_EVENT, callback)
  window.addEventListener('storage', callback)
  return () => {
    window.removeEventListener(CHANGE_EVENT, callback)
    window.removeEventListener('storage', callback)
  }
}

const EMPTY_RECENT_PLAYS: RecentPlay[] = []
let cachedRecentPlaysKey = ''
let cachedRecentPlays: RecentPlay[] = EMPTY_RECENT_PLAYS

function getRecentPlaysSnapshot(): RecentPlay[] {
  const next = safeRead()
  const nextKey = JSON.stringify(next)
  if (nextKey !== cachedRecentPlaysKey) {
    cachedRecentPlaysKey = nextKey
    cachedRecentPlays = next
  }
  return cachedRecentPlays
}

function getRecentPlaysServerSnapshot(): RecentPlay[] {
  return EMPTY_RECENT_PLAYS
}

/** Read-only React hook returning the user's recent plays. SSR-safe. */
export function useRecentPlays(): RecentPlay[] {
  return useSyncExternalStore(
    subscribeRecentPlays,
    getRecentPlaysSnapshot,
    getRecentPlaysServerSnapshot,
  )
}

/** Test-only: wipe the store. */
export function __clearRecentPlaysForTests(): void {
  removeStorage(STORAGE_KEY)
}
